
import { validateBACnetValue } from "../modal.helpers";
import { EBACnetDataTypes } from "../modal.types";
import { EPeriodTypes } from "./exception.types";
import { IEvent, IExclude } from "@lib/store/src/calendar";
import { getSelectOptions } from "./exception-form.constants";
import { IDateEntry, IDateEntryFormat, Optional } from "@lib/types";
import i18next from "i18next";

export const getEPeriodTypesLabels = (): { [key in EPeriodTypes]: string } => ({
  [EPeriodTypes.DATE]: i18next.t($ => $.scheduleBacnet.date),
  [EPeriodTypes.DATE_RANGE]: i18next.t($ => $.scheduleBacnet.range),
  [EPeriodTypes.WEEK_AND_DAY]: i18next.t($ => $.scheduleBacnet.weekAndDay),
  // [EPeriodTypes.CAL_REF]: "Ссылка календаря", не реализован
});

/**
 * Заполнение Select опциями
 *  @param {Element} select DOM-element select для заполнения
 *  @param {EPeriodTypes | string} value DOM-element select для заполнения
 *  @param {[string, string][]} data Список опций
 *  @returns {Element} - Заполненный "fw-select"
 */
export const initSelectOptions = (select: Element, value?: EPeriodTypes | string, data = Object.entries(getEPeriodTypesLabels())) => {
  if (!select) return null;
  select.innerHTML = ""; 
  
  if (data.length > 0) {
    data.forEach(([value, label]) => {
      const selectOption = document.createElement("option") as HTMLOptionElement;
      selectOption.textContent = label;
      selectOption.value = String(value);
      select.appendChild(selectOption);
    });
  } else {
    const selectOption = document.createElement("option") as HTMLOptionElement;
    selectOption.textContent = i18next.t($ => $.scheduleBacnet.valuesNotFound);
    selectOption.disabled = true;
    select.appendChild(selectOption);
  }

  select.value = value;
  
  return select;
};

/**
 * Валидация формы на простые ошибки и по типу значений
 * @returns {string} - Текст ошибки
 */
export const getFormError = (eventsState: IEvent[]) => {
  if (eventsState.length === 0) {
    return i18next.t($ => $.scheduleBacnet.errorNoEventsAdded);
  }

  for (const week of eventsState) {
    if (!week.time.hours || !week.time.minutes) {
      return i18next.t($ => $.scheduleBacnet.errorNoTimeSelected);
    }
    if (week.dataType === null) {
      return i18next.t($ => $.scheduleBacnet.errorNoDataTypeSelected);
    }
    if (week.value === null && week.dataType !== EBACnetDataTypes.NULL) {
      return i18next.t($ => $.scheduleBacnet.errorNoValueSelected);
    }

    if (validateBACnetValue(week.value, week.dataType)) {
      return i18next.t($ => $.scheduleBacnet.errorValuesDoNotMatchType);
    }
  }

  // Валидация периода

  return "";
};

export function formatExceptionText(period: IDateEntry): string {
  const findText = (category: keyof ReturnType<typeof getSelectOptions>, value: string): string => {
    const option = getSelectOptions()[category].find(item => item.value === value);
    return option ? option.text : i18next.t($ => $.scheduleBacnet.unknownValue);;
  };

  switch (period?.type) {
    case EPeriodTypes.DATE: {
      const { weekDay, day, month, year } = period?.start;
      const parts = [];

      parts.push(findText('dayOfWeek', weekDay.toString()));
      if (day === '**' || (+day >= 32 && +day <= 34)) {
        parts.push(findText('dayOfMonth', day.toString()));
      } else {
        parts.push(`${findText('dayOfMonth', day.toString())} ${i18next.t($ => $.scheduleBacnet.dayOfMonth).toLowerCase()}`);
      }
      parts.push(findText('month', month.toString()));
      parts.push(year === '****' ? findText('year', year.toString()) : year);

      return parts.length > 0 ? parts.join(', ') : i18next.t($ => $.scheduleBacnet.anyDayAnyMonthAnyYear);
    }

    case EPeriodTypes.DATE_RANGE: {
      const { start, end } = period;
      const formatPart = (params: Optional<IDateEntryFormat>) => {
        const parts = [];

        parts.push(findText('dayOfWeek', params.weekDay.toString()));
        if (params.day === '**' || (+params.day >= 32 && +params.day <= 34)) {
          parts.push(findText('dayOfMonth', params.day.toString()));
        } else {
          parts.push(`${findText('dayOfMonth', params.day.toString())} ${i18next.t($ => $.scheduleBacnet.dayOfMonth).toLowerCase()}`);
        }
        parts.push(findText('month', params.month.toString()));
        parts.push(params.year === '****' ? findText('year', params.year.toString()) : params.year);
        
        return parts.join(', ');
      };

      const startText = formatPart(start);
      const endText = formatPart(end);
      return `${startText} - ${endText}`;
    }

    case EPeriodTypes.WEEK_AND_DAY: {
      const { weekDay, week, month } = period?.start;
      const parts = [];

      parts.push(findText('dayOfWeek', weekDay.toString()));
      parts.push(findText('weekOptions', week.toString()));
      parts.push(findText('month', month.toString()));

      return parts.join(', ');
    }

    case EPeriodTypes.CAL_REF: {
      return `${i18next.t($ => $.scheduleBacnet.calendarLink)}: ${period?.calRef}`
    }

    default:
      throw new Error('Неизвестный тип периода');
  }
}

export function sortExceptionsByPeriodType(data: Array<IExclude>, selectedTypes: Array<EPeriodTypes> = []): Array<IExclude> {
  // 1. Фильтрация по выбранным типам периодов
  const filtered = selectedTypes.length > 0
    ? data.filter(item => selectedTypes.includes(item.period.type))
    : [...data];

  // 2. Сортировка
  return filtered.sort((a, b) => {
    // Сначала по типу периода (0-3)
    if (a.period.type !== b.period.type) {
      return a.period.type - b.period.type;
    }

    // 1. Год
    const yearCompare = compareYear(a.period.start.year, b.period.start.year);
    if (yearCompare !== 0) return yearCompare;

    // 2. Месяц
    const monthCompare = compareMonth(a.period.start.month, b.period.start.month);
    if (monthCompare !== 0) return monthCompare;

    // 3. Неделя (если есть в периоде)
    if ('week' in a.period.start && 'week' in b.period.start) {
      const weekCompare = compareWeek(a.period.start.week!, b.period.start.week!);
      if (weekCompare !== 0) return weekCompare;
    }

    // 4. День месяца
    const dayCompare = compareDay(a.period.start.day, b.period.start.day);
    if (dayCompare !== 0) return dayCompare;

    // 5. День недели
    return compareWeekDay(a.period.start.weekDay, b.period.start.weekDay);
  });
}

// Вспомогательные функции сравнения
function compareYear(a: string, b: string): number {
  if (a === b) return 0;
  if (a === '****') return 1;  // 'Любой' год идет после
  if (b === '****') return -1;
  return parseInt(a) - parseInt(b); // Конкретные годы по возрастанию
}

function compareMonth(a: string, b: string): number {
  if (a === b) return 0;
  
  // Порядок: конкретные > нечетные (13) > четные (14) > любые (**)
  const order: Record<string, number> = {
    '**': 3,
    '13': 2,
    '14': 1
  };

  const aVal = order[a] ?? 0; // Конкретные месяцы (1-12) = 0 (высший приоритет)
  const bVal = order[b] ?? 0;
  
  if (aVal !== bVal) return aVal - bVal;
  return parseInt(a) - parseInt(b); // Сортировка конкретных месяцев
}

function compareWeek(a: string, b: string): number {
  if (a === b) return 0;
  
  // Порядок: конкретные > последние 7 дней (6) > любые (*)
  if (a === '*') return 1;
  if (b === '*') return -1;
  if (a === '6') return -1;
  if (b === '6') return 1;
  return parseInt(a) - parseInt(b); // Конкретные недели по возрастанию
}

function compareDay(a: string, b: string): number {
  if (a === b) return 0;
  
  // Порядок: конкретные > последний (32) > нечетные (33) > четные (34) > любые (**)
  const order: Record<string, number> = {
    '**': 4,
    '32': 3,
    '33': 2,
    '34': 1
  };

  const aVal = order[a] ?? 0; // Конкретные дни = 0 (высший приоритет)
  const bVal = order[b] ?? 0;
  
  if (aVal !== bVal) return aVal - bVal;
  return parseInt(a) - parseInt(b); // Конкретные дни по возрастанию
}

function compareWeekDay(a: string, b: string): number {
  if (a === b) return 0;
  if (a === '*') return 1;  // 'Любой' день недели идет после
  if (b === '*') return -1;
  return parseInt(a) - parseInt(b); // Конкретные дни недели по возрастанию (1-7)
}